import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Package, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface InventoryDrug {
  id: string;
  name: string;
  strength?: string | null;
  form?: string | null;
  unit?: string | null;
  genericName?: string | null;
  isActive?: boolean;
}

interface DrugInventorySearchProps {
  onDrugSelect: (drug: InventoryDrug) => void;
  placeholder?: string;
  className?: string;
  /** Controlled display label after selection (optional) */
  selectedLabel?: string;
  onClear?: () => void;
}

function formatDrugLabel(drug: InventoryDrug): string {
  const parts = [drug.name, drug.strength, drug.form].filter(Boolean);
  return parts.join(' · ');
}

export function DrugInventorySearch({
  onDrugSelect,
  placeholder = 'Search pharmacy inventory…',
  className,
  selectedLabel,
  onClear,
}: DrugInventorySearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const trimmed = searchQuery.trim();
  const canSearch = trimmed.length >= 2;

  const { data: results = [], isFetching } = useQuery<InventoryDrug[]>({
    queryKey: ['pharmacy-drugs-search', trimmed],
    queryFn: async () => {
      const res = await api.get<{ data?: InventoryDrug[] }>('/api/prescription/drugs', {
        params: { search: trimmed, isActive: true },
      });
      return res.data?.data ?? [];
    },
    enabled: canSearch && open,
    staleTime: 30_000,
  });

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const handleSelect = (drug: InventoryDrug) => {
    onDrugSelect(drug);
    setSearchQuery('');
    setOpen(false);
  };

  if (selectedLabel) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-2 text-sm',
          className
        )}
      >
        <Package className="h-4 w-4 shrink-0 text-emerald-600" />
        <span className="flex-1 truncate font-medium text-slate-800">{selectedLabel}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 text-slate-500 hover:text-red-600"
          onClick={() => onClear?.()}
          aria-label="Clear selected medicine"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={placeholder}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            className="pl-9 pr-9"
          />
          {searchQuery && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              onClick={() => {
                setSearchQuery('');
                setOpen(false);
              }}
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {open && (
          <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
            Close
          </Button>
        )}
      </div>

      {open && (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl">
          <div className="sticky top-0 border-b border-slate-100 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-500">
            Pharmacy inventory
            {canSearch && !isFetching && ` · ${results.length} match${results.length === 1 ? '' : 'es'}`}
          </div>
          {!canSearch ? (
            <p className="px-3 py-4 text-sm text-slate-500">Type at least 2 characters to search medicines.</p>
          ) : isFetching ? (
            <div className="flex items-center gap-2 px-3 py-4 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching…
            </div>
          ) : results.length === 0 ? (
            <p className="px-3 py-4 text-sm text-slate-500">No medicines found in inventory.</p>
          ) : (
            results.map((drug) => (
              <button
                key={drug.id}
                type="button"
                className="flex w-full flex-col gap-0.5 border-b border-slate-50 px-3 py-2.5 text-left last:border-0 hover:bg-teal-50/80"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(drug)}
              >
                <span className="text-sm font-medium text-slate-900">{formatDrugLabel(drug)}</span>
                {drug.genericName && drug.genericName !== drug.name && (
                  <span className="text-xs text-slate-500">{drug.genericName}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export { formatDrugLabel };
